import { useState, useEffect } from 'react';
import { CreditCard, Upload, Save, Image as ImageIcon } from 'lucide-react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface PaymentSettings {
  promptpay: {
    id: string;
    name: string;
    qrCodeUrl?: string;
  };
  bank: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
  updatedAt?: any;
}

export default function PaymentSettingsSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PaymentSettings>({
    promptpay: {
      id: '',
      name: '',
      qrCodeUrl: ''
    },
    bank: {
      bankName: '',
      accountNumber: '',
      accountName: ''
    },
    updatedAt: null
  });
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [qrPreview, setQrPreview] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'paymentSettings', 'default'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as PaymentSettings;
        setSettings(data);
        setQrPreview(data.promptpay?.qrCodeUrl || '');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQrFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setQrFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const qrCodeUrl = qrFile ? qrPreview : (settings.promptpay?.qrCodeUrl || '');

      const settingsData: PaymentSettings = {
        promptpay: {
          id: settings.promptpay.id,
          name: settings.promptpay.name,
          qrCodeUrl
        },
        bank: {
          bankName: settings.bank.bankName,
          accountNumber: settings.bank.accountNumber,
          accountName: settings.bank.accountName
        },
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'paymentSettings', 'default'), settingsData);

      alert('✅ บันทึกข้อมูลสำเร็จ');
      loadSettings();
      setQrFile(null);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('เกิดข้อผิดพลาด: ' + error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-pink-600 rounded-xl flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-textDark">ตั้งค่าการชำระเงิน</h2>
            <p className="text-sm text-textLight">จัดการข้อมูลบัญชีธนาคารและ QR Code</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* QR Code Upload */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            QR Code พร้อมเพย์
          </label>
          <div className="space-y-4">
            {qrPreview && (
              <div className="flex justify-center">
                <img
                  src={qrPreview}
                  alt="QR Code Preview"
                  className="w-64 h-64 object-contain border-2 border-gray-200 rounded-xl"
                />
              </div>
            )}
            <div className="flex items-center gap-4">
              <label className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-all">
                  <Upload className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-600 font-semibold">
                    {qrFile ? qrFile.name : 'อัปโหลด QR Code'}
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleQrFileChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-xs text-textLight">
              รองรับไฟล์: JPG, PNG, GIF (แนะนำขนาด 512x512 px)
            </p>
          </div>
        </div>

        {/* PromptPay ID */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            PromptPay ID
          </label>
          <input
            type="text"
            value={settings.promptpay.id}
            onChange={(e) => setSettings({ ...settings, promptpay: { ...settings.promptpay, id: e.target.value } })}
            placeholder="เช่น 0XX-XXX-XXXX"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* PromptPay Name */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            ชื่อบัญชี PromptPay
          </label>
          <input
            type="text"
            value={settings.promptpay.name}
            onChange={(e) => setSettings({ ...settings, promptpay: { ...settings.promptpay, name: e.target.value } })}
            placeholder="เช่น EduMi Platform"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* Bank Name */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            ธนาคาร
          </label>
          <input
            type="text"
            value={settings.bank.bankName}
            onChange={(e) => setSettings({ ...settings, bank: { ...settings.bank, bankName: e.target.value } })}
            placeholder="เช่น ธนาคารกสิกรไทย"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* Account Name */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            ชื่อบัญชีธนาคาร
          </label>
          <input
            type="text"
            value={settings.bank.accountName}
            onChange={(e) => setSettings({ ...settings, bank: { ...settings.bank, accountName: e.target.value } })}
            placeholder="เช่น บริษัท EduMi จำกัด"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* Account Number */}
        <div>
          <label className="block text-sm font-semibold text-textDark mb-2">
            เลขที่บัญชี
          </label>
          <input
            type="text"
            value={settings.bank.accountNumber}
            onChange={(e) => setSettings({ ...settings, bank: { ...settings.bank, accountNumber: e.target.value } })}
            placeholder="เช่น 123-4-56789-0"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
          />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            คำแนะนำ
          </h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• QR Code จะแสดงในหน้าชำระเงินของผู้ใช้</li>
            <li>• ข้อมูลบัญชีธนาคารจะแสดงพร้อมกับ QR Code</li>
            <li>• ผู้ใช้สามารถโอนเงินผ่าน QR Code หรือโอนธนาคารได้</li>
            <li>• อัปเดตข้อมูลได้ตลอดเวลา</li>
          </ul>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
        </button>
      </div>
    </div>
  );
}
